import React, { useState, useEffect, useRef } from "react";
import { logResponse } from "../utils/logger";
import { fetchRegisterExistingCustomer } from "../api";
import { useNavigate } from "react-router-dom";
import SubmitButton from "../components/SubmitButton";

const RegisterExistingCustomerForm = () => {
  const formRef = useRef();
  const navigate = useNavigate();

  const [accessToken, setAccessToken] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  const eras = [
    { name: "令和", start: 2019 },
    { name: "平成", start: 1989 },
    { name: "昭和", start: 1926 },
    { name: "大正", start: 1912 },
  ];

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 106;
    const endYear = currentYear - 19;
    const options = [];

    for (let y = startYear; y <= endYear; y++) {
      const era = eras.find((e) => y >= e.start);
      const eraYear = y - era.start + 1;
      const label = `${y}（${era.name}${eraYear === 1 ? "元" : eraYear}）年`;
      options.push({ value: y.toString(), label });
    }

    return options;
  };

  const yearOptions = generateYearOptions();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token");
    const tokenFromStorage = localStorage.getItem("access_token");
    setAccessToken(tokenFromUrl || tokenFromStorage || "");
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    if (!accessToken) {
      setErrors({ general: "アクセストークンがありません。メールからアクセスしてください。" });
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData(formRef.current);
    const newErrors = {};

    // 氏名（必須＋全角チェック）
    const name = formData.get("customer_name");
    if (!name) {
      newErrors.customer_name = "氏名は必須です";
    } else if (!/^[^\u0020-\u007E\uFF61-\uFF9F]+$/.test(name)) {
      newErrors.customer_name = "氏名は全角で入力してください";
    }

    // 電話番号（必須＋数字チェック）
    const phone = formData.get("phone_number");
    if (!phone) {
      newErrors.phone_number = "電話番号は必須です";
    } else if (!/^[0-9-]{10,}$/.test(phone)) {
      newErrors.phone_number = "電話番号は10桁以上の半角数字とハイフンのみで入力してください";
    }

    // 生年月日（セレクト必須）
    if (!year || !month || !day) {
      newErrors.birth_date = "生年月日を選択してください";
    }

    // 郵便番号（必須＋数字チェック）
    const postalCode = formData.get("postal_code");
    if (!postalCode) {
      newErrors.postal_code = "郵便番号は必須です";
    } else if (!/^\d{3}-?\d{4}$/.test(postalCode)) {
      newErrors.postal_code = "郵便番号は半角数字7桁（例：1234567 または 123-4567）で入力してください";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    setErrors({});

    // 生年月日を組み立てて追加
    const y = formData.get("birth-year");
    const m = String(formData.get("birth-month")).padStart(2, "0");
    const d = String(formData.get("birth-day")).padStart(2, "0");
    formData.set("birth_date", `${y}${m}${d}`);

    // アクセストークンを追加
    formData.set("access_token", accessToken);

    const data = Object.fromEntries(formData.entries());

    fetchRegisterExistingCustomer(accessToken, data)
      .then((res) => {
        logResponse("token:", accessToken);
        logResponse("req:", data);
        logResponse("res:", res);
        if (res && res.message_id) {
          setTimeout(() => {
            navigate("/signup-thanks");
          }, 0);
        } else if (res && res.detail) {
          const message =
            res.detail === "Not authenticated"
              ? "アクセストークンがありません。メールからアクセスしてください。"
              : res.detail;
          console.error("登録失敗:", message);
          setErrors({ general: message });
        } else {
          console.error("登録失敗: 不明なレスポンス", res);
          setErrors({ general: "登録に失敗しました。時間を置いて再度お試しください。" });
        }
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      })
      .catch((err) => {
        console.error("通信エラー:", err);
        setErrors({ general: "通信エラーが発生しました。時間を置いて再度お試しください。" });
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      });
  };

  if (!accessToken) {
    return (
      <p className="alert alert-danger tc" role="alert">
        アクセストークンが見つかりません。<br />
        メールのリンクからアクセスしてください。<br />
        <a className="underline" href="/">トップページへ戻る</a>
      </p>
    );
  }

  return (
    <section className="section userinfo-section">
      <h2 className="section-title">
        <i><img src="assets/img/icon_pay.svg" alt="" /></i>
        お客様情報登録
      </h2>
      <div className="userinfo-group">
        <div className="group-wrapper">
          <div className="form-group">
            <form ref={formRef} onSubmit={handleSubmit}>
              <p className="explain error tc fs100">
                すでに入力が済んでいる場合は、再入力不要です。
                <br />
                数営業日以内にログインが可能となりますので、それまでお待ちくださいませ。
              </p>

              <div>
                <span className="column">氏名</span>
                <input type="text" name="customer_name" defaultValue="" />
                {errors.customer_name && <div className="error">{errors.customer_name}</div>}
              </div>

              <div>
                <span className="column">電話番号</span>
                <input type="tel" name="phone_number" defaultValue="" />
                {errors.phone_number && <div className="error">{errors.phone_number}</div>}
              </div>

              <div>
                <span className="column">生年月日</span>
                <div className="birthday">
                  <select name="birth-year" value={year} onChange={(e) => setYear(e.target.value)}>
                    <option value="">年</option>
                    {yearOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <select name="birth-month" value={month} onChange={(e) => setMonth(e.target.value)}>
                    <option value="">月</option>
                    {months.map((m) => (
                      <option key={m} value={m}>{m}月</option>
                    ))}
                  </select>
                  <select name="birth-day" value={day} onChange={(e) => setDay(e.target.value)}>
                    <option value="">日</option>
                    {days.map((d) => (
                      <option key={d} value={d}>{d}日</option>
                    ))}
                  </select>
                </div>
                {errors.birth_date && <div className="error">{errors.birth_date}</div>}
              </div>

              <div>
                <span className="column">郵便番号</span>
              </div>
              <div>
                <span className="column small inline_block mt5">〒</span>
                <input type="text" name="postal_code" defaultValue="" />
                {errors.postal_code && <div className="error">{errors.postal_code}</div>}
              </div>

              <div className="submit btn-green">
                <SubmitButton
                  type="submit"
                  isSubmitting={isSubmitting}
                  label="お客様情報登録"
                  className="btn-green"
                  wrap={false}
                />
              </div>

              {errors.general && (
                <p className="text-danger mt10" style={{ fontSize: "0.9em" }}>
                  {errors.general}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegisterExistingCustomerForm;
